import {
  ArrowDown,
  ArrowUp,
  CircleSlash,
  ExternalLink,
  Image as ImageIcon,
  Link as LinkIcon,
  Pencil,
  Trash2,
} from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import { useTranslation } from '@dukanti/shared';
import type { StorePromotionalAdWithPreview, TranslationKey } from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export interface PromotionalAdsListProps {
  readonly ads: readonly StorePromotionalAdWithPreview[];
  readonly isReordering?: boolean;
  readonly onReorder?: (orderedAdIds: string[]) => void | Promise<void>;
  readonly onEdit?: (ad: StorePromotionalAdWithPreview) => void;
  readonly onDelete?: (ad: StorePromotionalAdWithPreview) => void;
  readonly onMove?: (ad: StorePromotionalAdWithPreview, direction: 'up' | 'down') => void;
  /**
   * Epic #228 P7: retained ads of a store that can no longer author them. No
   * move, edit or delete control is rendered, so no callback can fire.
   */
  readonly readOnly?: boolean;
}

function getDisplayName(ad: StorePromotionalAdWithPreview, fallback: string): string {
  const title = ad.title?.trim();
  if (title) return title;
  const alt = ad.alt_text?.trim();
  return alt || fallback;
}

export function PromotionalAdsList({
  ads,
  isReordering = false,
  onReorder,
  onEdit,
  onDelete,
  onMove,
  readOnly = false,
}: PromotionalAdsListProps) {
  const { t, dir } = useTranslation();

  function handleMove(ad: StorePromotionalAdWithPreview, direction: 'up' | 'down'): void {
    if (onMove) {
      onMove(ad, direction);
      return;
    }
    if (!onReorder) return;
    const currentIndex = ads.findIndex((item) => item.id === ad.id);
    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= ads.length) return;
    const next = [...ads];
    const current = next[currentIndex];
    const target = next[nextIndex];
    if (!current || !target) return;
    next[currentIndex] = target;
    next[nextIndex] = current;
    void onReorder(next.map((item) => item.id));
  }

  function handleMoveMouseDown(
    event: MouseEvent<HTMLButtonElement>,
    ad: StorePromotionalAdWithPreview,
    direction: 'up' | 'down',
  ): void {
    if (event.button !== 0) return;
    event.preventDefault();
    handleMove(ad, direction);
  }

  function handleMoveKeyDown(
    event: KeyboardEvent<HTMLButtonElement>,
    ad: StorePromotionalAdWithPreview,
    direction: 'up' | 'down',
  ): void {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    handleMove(ad, direction);
  }

  return (
    <div
      dir={dir}
      data-testid="promotional-ads-list"
      data-readonly={readOnly ? 'true' : undefined}
      className="border-border bg-card shadow-admin-card overflow-hidden rounded-lg border"
    >
      <ul className="divide-border divide-y">
        {ads.map((ad, index) => {
          const isFirst = index === 0;
          const isLast = index === ads.length - 1;
          const displayName = getDisplayName(
            ad,
            t('admin.promotionalAds.list.untitled' as TranslationKey),
          );
          const destinationSummary = ad.destinationSummary;
          const destinationType = ad.destination.type;
          const destinationText = t(destinationSummary.key as TranslationKey, {
            value: destinationSummary.value ?? '',
          });

          return (
            <li
              key={ad.id}
              data-testid={`promotional-ad-row-${ad.id}`}
              className="flex flex-col gap-4 p-4 lg:flex-row lg:items-center"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                <div className="bg-admin-surface-muted border-border flex aspect-[3/1] w-full shrink-0 items-center justify-center overflow-hidden rounded-lg border sm:w-48">
                  {ad.preview_url ? (
                    <img
                      src={ad.preview_url}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <ImageIcon
                      className="text-muted-foreground size-7"
                      strokeWidth={1.5}
                      aria-hidden
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p
                      className="text-foreground min-w-0 truncate text-start font-medium"
                      data-testid={`promotional-ad-row-${ad.id}-title`}
                    >
                      {displayName}
                    </p>
                    <Badge variant={ad.is_active ? 'success' : 'neutral'}>
                      {t(
                        ad.is_active
                          ? ('admin.promotionalAds.status.active' as TranslationKey)
                          : ('admin.promotionalAds.status.inactive' as TranslationKey),
                      )}
                    </Badge>
                  </div>
                  <p className="text-muted-foreground mt-1 text-start text-xs">{index + 1}</p>
                  <p className="text-muted-foreground mt-2 flex min-w-0 items-center gap-1.5 text-start text-xs">
                    {destinationType === 'external' ? (
                      <ExternalLink className="size-3.5 shrink-0" aria-hidden />
                    ) : destinationType === 'none' ? (
                      <CircleSlash className="size-3.5 shrink-0" aria-hidden />
                    ) : (
                      <LinkIcon className="size-3.5 shrink-0" aria-hidden />
                    )}
                    <span className="truncate">{destinationText}</span>
                  </p>
                </div>
              </div>

              {readOnly ? null : (
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isFirst || isReordering}
                    data-testid="promotional-ad-move-up"
                    aria-label={t('admin.promotionalAds.actions.moveUp' as TranslationKey, {
                      title: displayName,
                    })}
                    onKeyDown={(event) => handleMoveKeyDown(event, ad, 'up')}
                    onMouseDown={(event) => handleMoveMouseDown(event, ad, 'up')}
                  >
                    <ArrowUp className="size-4" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isLast || isReordering}
                    data-testid="promotional-ad-move-down"
                    aria-label={t('admin.promotionalAds.actions.moveDown' as TranslationKey, {
                      title: displayName,
                    })}
                    onKeyDown={(event) => handleMoveKeyDown(event, ad, 'down')}
                    onMouseDown={(event) => handleMoveMouseDown(event, ad, 'down')}
                  >
                    <ArrowDown className="size-4" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('admin.promotionalAds.actions.edit' as TranslationKey, {
                      title: displayName,
                    })}
                    onClick={() => onEdit?.(ad)}
                  >
                    <Pencil className="size-4" aria-hidden />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-sand-600 hover:text-terracotta dark:text-sand-300"
                    aria-label={t('admin.promotionalAds.actions.delete' as TranslationKey, {
                      title: displayName,
                    })}
                    onClick={() => onDelete?.(ad)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
